import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@heroui/react';
import { cn, getNodeTestProps } from '@/utils';
import { formatGel } from '../../sellerMeta';
import { QUICK_CASH_AMOUNTS } from '../paymentMeta';
import type { PaymentFlowValue } from '../usePaymentFlow';
import { Numpad, type NumpadProps } from './Numpad';
import { PaymentEntryNotice } from './PaymentEntryNotice';

type CashSectionProps = PaymentFlowValue & Pick<NumpadProps, 'flashedKey'>;

/**
 * Cash tender step (render-only): a large centred received-amount display, one-tap
 * shortcuts (exact + common notes), the shared touch numpad, and the change owed
 * once the tender covers the due. A reserved slot below the numpad shows
 * why the tender can't confirm without moving the keys. A tapped chip, like a
 * numpad key, never takes focus (`preventFocusOnPress`): a focused chip would
 * take the next physical `Enter` as a second press instead of confirming; `Tab`
 * still reaches it. Every value comes from `usePaymentFlow`.
 */
export const CashSection: React.FC<CashSectionProps> = ({
    cashEntry,
    change,
    entryNoticeKey,
    onPadKey,
    onPadDelete,
    padDeleteDisabled,
    setQuickCash,
    setExactCash,
    flashedKey,
}) => {
    const { t } = useTranslation('pages');
    const isEmpty = cashEntry === '';

    return (
        <div className="flex flex-col gap-3">
            <Typography color="muted" type="body-sm">
                {t('seller.payment.received')}
            </Typography>

            <div
                className={cn(
                    'rounded-2xl bg-surface-secondary px-5 py-4 text-center font-mono text-4xl font-bold tabular-nums leading-none',
                    isEmpty && 'text-muted',
                )}
                {...getNodeTestProps('seller-pay-cash-display')}
            >
                {`₾ ${isEmpty ? '0' : cashEntry}`}
            </div>

            <div className="flex flex-wrap justify-center gap-2">
                <Button
                    {...getNodeTestProps('seller-pay-cash-exact')}
                    onPress={setExactCash}
                    preventFocusOnPress
                    size="sm"
                    variant="outline"
                >
                    {t('seller.payment.exact')}
                </Button>
                {QUICK_CASH_AMOUNTS.map((amount) => (
                    <Button
                        key={amount}
                        {...getNodeTestProps(`seller-pay-cash-quick-${amount}`)}
                        onPress={() => setQuickCash(amount)}
                        preventFocusOnPress
                        size="sm"
                        variant="outline"
                    >
                        {`₾${amount}`}
                    </Button>
                ))}
            </div>

            <Numpad
                deleteDisabled={padDeleteDisabled}
                flashedKey={flashedKey}
                onDelete={onPadDelete}
                onKey={onPadKey}
            />

            <PaymentEntryNotice messageKey={entryNoticeKey} method="cash" />

            {change > 0 ? (
                <div
                    className="flex items-center justify-between rounded-xl bg-surface-secondary px-4 py-3"
                    {...getNodeTestProps('seller-pay-change')}
                >
                    <Typography color="muted" type="body-sm">
                        {t('seller.payment.change')}
                    </Typography>
                    <Typography
                        className="text-success tabular-nums"
                        type="h5"
                        weight="bold"
                    >
                        {formatGel(change)}
                    </Typography>
                </div>
            ) : null}
        </div>
    );
};
